import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

export const WaldineTauch = () => {
  const majorWorks = [
    {
      name: "The First Inhabitant",
      year: "1915",
      location: "Commerce Street Bridge, San Antonio",
      description: "A striking 7-foot bronze of a Native American in full feathered headdress, originally featuring drinking fountains in small bowls held in each hand. Her first major public commission in San Antonio.",
      image: "https://memoriesofsanantonio.com/wp-content/uploads/2022/01/the-first-inhabitant-statue.jpeg"
    },
    {
      name: "The First Shot Fired For Texas Independence",
      year: "1935",
      location: "Near Gonzales, Texas",
      description: "A life-sized bronze bas-relief set in granite commemorating the Battle of Gonzales. She won this commission over renowned sculptor Gutzon Borglum, creator of Mount Rushmore.",
      image: "https://memoriesofsanantonio.com/wp-content/uploads/2022/01/gonzales-memorial.jpeg"
    },
    {
      name: "Moses Austin",
      year: "1937-38",
      location: "Behind City Hall, San Antonio",
      description: "A 10-foot statue with bas-reliefs around the base depicting scenes from Austin's life. The inscription took weeks to carve—and had to be redone when given incorrect information.",
      image: "https://memoriesofsanantonio.com/wp-content/uploads/2022/01/moses-austin-statue.jpeg"
    },
    {
      name: "Douglas MacArthur",
      year: "1966-68",
      location: "Howard Payne University, Brownwood",
      description: "An 8-foot bronze statue that became her most desired commission, received at age 74. She famously created the initial nude figure before adding military attire.",
      image: "https://memoriesofsanantonio.com/wp-content/uploads/2022/01/gen-douglas-macarthur-statue.jpeg"
    },
    {
      name: "Education Reflects Responsibility Into The World",
      year: "1965",
      location: "Trinity University, San Antonio",
      description: "A heroic-sized bronze created for the campus where she had taught. The figure is a composite of three different male models—each contributing their best features.",
      image: "https://memoriesofsanantonio.com/wp-content/uploads/2022/01/trinity-sculpture.jpeg"
    },
    {
      name: "Pippa Passes",
      year: "1956",
      location: "Baylor University, Waco",
      description: "A bronze, life-sized high relief at Baylor, representing one of her most celebrated works in classical style.",
      image: "https://www.tshaonline.org/images/handbook/entries/TT/Tauch%20George%20Washington%20(Photo).jpeg"
    }
  ];

  const timeline = [
    { year: "1892", event: "Born January 28 in Schulenburg, Texas, daughter of photographer William Tauch" },
    { year: "1899", event: "At age 7, became enchanted by a carved German ivory bookmark and began sculpting" },
    { year: "1910", event: "Left Brady to study with Pompeo Coppini in San Antonio, just two weeks before graduating high school" },
    { year: "1911", event: "Secured her first public commission—a bas-relief for Brownwood Library" },
    { year: "1915", event: "Created 'The First Inhabitant' for Commerce Street Bridge in San Antonio" },
    { year: "1918-22", event: "Worked with Coppini in his Chicago studio" },
    { year: "1923-35", event: "Lived in New York, assisting Coppini with the Littlefield Fountain and creating her own works" },
    { year: "1935", event: "Returned to San Antonio for Texas Centennial commissions" },
    { year: "1936", event: "Built shared studio with Coppini at 115 Melrose Place, San Antonio" },
    { year: "1941", event: "Received honorary doctorate from Howard Payne College" },
    { year: "1943-45", event: "Taught at Trinity University alongside Coppini" },
    { year: "1945", event: "Co-founded the Academy of Fine Art with Coppini (later Coppini Academy)" },
    { year: "1964", event: "Elected Fellow of the National Sculpture Society of New York City" },
    { year: "1969", event: "Texas Senate awarded her Recognition Certificate for contributions to Texas culture" },
    { year: "1986", event: "Passed away March 31 in San Antonio, buried with the Coppinis at Sunset Memorial Park" }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="waldine-tauch-page">
      <SEOHead
        title="Waldine Tauch — Sculptor & Co-Founder"
        description="Learn about Waldine Tauch (1892–1986), acclaimed Texas sculptor and co-steward of The Coppini Academy of Fine Art in San Antonio."
        canonicalPath="/about/waldine-tauch"
      />
      {/* Hero */}
      <section className="bg-gradient-to-b from-cafa-blue-900 to-cafa-blue-800 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <p className="text-cafa-blue-200 font-medium mb-2">1892 - 1986</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif mb-6">Waldine Amanda Tauch</h1>
              <p className="text-xl text-cafa-blue-100 leading-relaxed">
                Pioneering Texas sculptor who defied expectations to create heroic public monuments. 
                Protégée of Pompeo Coppini and co-founder of the Coppini Academy of Fine Art.
              </p>
              <p className="text-cafa-blue-200 mt-4">
                <Link to="/about/pompeo-coppini" className="text-cafa-blue-200 hover:text-cafa-blue-200 underline">
                  ← Learn about her mentor, Pompeo Coppini
                </Link>
              </p>
            </div>
            <div className="text-center">
              <img 
                src="https://memoriesofsanantonio.com/wp-content/uploads/2022/01/waldine-in-studio.jpeg"
                alt="Waldine Tauch in the Coppini studio"
                className="w-64 h-auto mx-auto rounded-lg shadow-2xl border-4 border-white/20"
              />
              <p className="text-sm text-cafa-blue-200 mt-3">Waldine in the Coppini studio</p>
            </div>
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">A Natural Talent Discovered</h2>
          
          <div className="prose prose-lg max-w-none text-stone-700">
            <p className="mb-6">
              Waldine Amanda Tauch (pronounced to rhyme with "cow") entered the world on January 28, 1892, 
              in the small Texas town of Schulenburg. Her father William, a photographer by trade, nurtured 
              her artistic inclinations from an early age—providing photographs for her to sketch and encouraging 
              her creative exploration. Her mother, however, held more traditional hopes, wishing her daughter 
              would marry and raise a family.
            </p>
            
            <p className="mb-6">
              At the tender age of seven, young Waldine became captivated by an exquisitely carved German ivory 
              bookmark brought to their home by a family friend. That moment sparked a lifelong passion. She began 
              sculpting immediately, working with whatever materials she could find—clay, chalk, soap, wood, and 
              eventually stone.
            </p>

            <p className="mb-6">
              When her family relocated to Brady, Texas, due to her father's declining health, the local 
              <strong> Brady Tuesday Study Club</strong> recognized her exceptional abilities. After she created 
              a butter sculpture centerpiece for one of their luncheons that left members astonished, they began 
              raising funds for her artistic education. This effort led to her introduction to the renowned sculptor 
              <strong> Pompeo Coppini</strong>, who maintained a studio in San Antonio.
            </p>
          </div>
        </div>
      </section>

      {/* Mentor Relationship */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8">From Student to Partner</h2>
          
          <div className="prose prose-lg max-w-none text-stone-700">
            <p className="mb-6">
              In 1910, just two weeks before she would have graduated high school, the eighteen-year-old Waldine 
              left everything behind to study with Coppini in San Antonio. Coppini was immediately struck by her 
              raw talent. When the club's funding eventually ran dry, forcing her to return to Brady, Coppini 
              refused to let her promising career end. He brought her back to San Antonio and taught her without 
              charge, raising her as a foster daughter in his own home.
            </p>

            <p className="mb-6">
              Under Coppini's tutelage, Waldine absorbed the principles of classical sculpture and developed a 
              naturalistic style. Like her mentor, she became an outspoken champion of traditional art, once 
              famously declaring abstract art to be "an irritation to the eye and an insult to the mind."
            </p>

            <p className="mb-6">
              When Waldine announced her ambition to create large-scale heroic public monuments, Coppini initially 
              pushed back. Standing just five feet, five and a half inches tall, she seemed physically ill-suited 
              for the demanding work of sculpting larger-than-life figures. But Waldine refused to be deterred. 
              Those who watched her work over the years marveled at her strength and stamina—climbing tall ladders, 
              constructing heavy armatures from boards and pipes, and packing them with clay to bring her visions to life.
            </p>
          </div>
        </div>
      </section>

      {/* Major Works Gallery */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-4 text-center">Notable Sculptures</h2>
          <p className="text-stone-600 text-center mb-12 max-w-2xl mx-auto">
            Throughout her seven-decade career, Waldine created works displayed across Texas and beyond, 
            earning recognition from the National Sculpture Society and the Texas Senate.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {majorWorks.map((work, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border border-stone-200 hover:shadow-lg transition-shadow">
                <div className="h-56 overflow-hidden bg-stone-100">
                  <img 
                    src={work.image}
                    alt={work.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-cafa-red font-bold">{work.year}</span>
                    <span className="text-xs text-stone-500 bg-stone-100 px-2 py-1 rounded">{work.location}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">{work.name}</h3>
                  <p className="text-sm text-stone-600">{work.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Highlight */}
      <section className="py-16 bg-cafa-blue-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://memoriesofsanantonio.com/wp-content/uploads/2022/01/gonzales-memorial.jpeg"
                alt="First Shot Fired For Texas Independence Memorial"
                className="w-full rounded-lg shadow-2xl"
              />
            </div>
            <div>
              <p className="text-cafa-blue-200 font-medium mb-2">Texas Centennial Commission</p>
              <h2 className="text-3xl font-serif mb-6">Beating Mount Rushmore's Creator</h2>
              <p className="text-cafa-blue-100 mb-6 leading-relaxed">
                When the 1936 Texas Centennial celebration sparked a wave of commemorative commissions, 
                Waldine returned to San Antonio to compete. Her determination paid off spectacularly—she 
                won the commission for "The First Shot Fired For Texas Independence" over none other than 
                <strong> Gutzon Borglum</strong>, the nationally renowned sculptor responsible for Mount Rushmore.
              </p>
              <p className="text-cafa-blue-100 leading-relaxed">
                The life-sized bronze bas-relief, set in granite near the historic Battle of Gonzales site, 
                stands as a testament to her skill and the recognition she had earned among her peers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-12 text-center">Life Timeline</h2>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-cafa-blue-300"></div>
            
            <div className="space-y-6">
              {timeline.map((item, index) => (
                <div key={index} className="flex items-start gap-6 relative">
                  <div className="flex-shrink-0 w-16 text-right">
                    <span className="font-bold text-cafa-red">{item.year}</span>
                  </div>
                  <div className="flex-shrink-0 w-4 h-4 mt-1 rounded-full bg-cafa-red-500 border-4 border-stone-50 z-10"></div>
                  <p className="text-stone-700 flex-1 pb-2">{item.event}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Legacy */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">An Enduring Legacy</h2>
          
          <div className="prose prose-lg max-w-none text-stone-700">
            <p className="mb-6 text-center">
              Waldine continued sculpting and teaching well into her eighties, her passion undimmed even as 
              her eyesight began to fail. Beyond her artistic contributions, she traveled throughout Texas 
              promoting traditional art through lectures and demonstrations. She served on numerous arts 
              organizations including the National Sculpture Society, the San Antonio Art League, and the 
              National Association of Women Painters and Sculptors.
            </p>
            
            <p className="mb-8 text-center">
              When she passed away on March 31, 1986, at the age of 94, she was laid to rest alongside 
              her mentors, Pompeo and Elizabeth Coppini, at Sunset Memorial Park—a fitting tribute to 
              a partnership that shaped Texas sculpture for generations.
            </p>
          </div>
          
          <div className="bg-stone-900 text-white p-8 rounded-xl text-center">
            <h3 className="text-2xl font-serif mb-4">Continuing Their Vision</h3>
            <p className="text-stone-300 mb-6">
              The Coppini Academy of Fine Art, which Waldine co-founded with Pompeo in 1945 and later 
              sponsored after his death, continues their shared mission of preserving classical art traditions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/classes/adult"
                className="inline-flex items-center justify-center px-6 py-3 bg-cafa-red hover:bg-cafa-red-700 text-white rounded-md font-medium transition-colors"
              >
                Explore Our Classes
              </Link>
              <Link 
                to="/about/pompeo-coppini"
                className="inline-flex items-center justify-center px-6 py-3 border border-white text-white hover:bg-white/10 rounded-md font-medium transition-colors"
              >
                Read About Pompeo Coppini
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
